import * as _ from 'lodash';
import * as utils from 'common/utils';
import { areTimestampsEqual } from './overlayPointSelection';
import { paginateAndFetchChartData } from './helpers';
import { measuredPeakForAlarmForm } from '../utils/alarmParameters';

export function createChartsViewChartDataOrchestrator({
  getState,
  setState,
  getProps,
  getParamsForChartsData,
  getWaveformAndSpectrum,
  setSelection,
  getDataFromTimestamp,
  refreshOverlayTraces,
  refreshOverlayPointsWithNewFeature,
  updateSelectedPoints,
  setChartSettingsInSession,
  setBinNosInSession,
  setCalendarInSession,
  setTimeStampInSession,
  getSpectrumMarkers
}) {
  const fetchChartData = (params, pagination, callBackFn, noOfRequests, uniqueKey) => (
    getProps().assetDetailsActions.getChartsData(params, pagination, noOfRequests, uniqueKey).then(
      (res) => {
        if (getProps().previewConfig) {
          const shouldResetData = !pagination || getState().config.uniqueKey !== uniqueKey;
          setState(prevState => ({
            config: {
              ...prevState.config,
              uniqueKey,
              trends_data: _.isEmpty(prevState.config.trends_data) || shouldResetData ?
                res :
                _.map(prevState.config.trends_data, (td, i) => {
                  if (!_.isObject(td)) return td;
                  return {
                    ...td,
                    trend_data: [...res[i].trend_data, ...td.trend_data].sort(
                      (a, b) => (a.x - b.x)
                    )
                  };
                })
            }
          }));
        }
        callBackFn(res);
      }
    )
  );

  const updatePreviewConfig = (tag, params) => {
    const { getTagSpectrum, getTagWaveform, getTagCepstrum } = getProps().assetDetailsActions;
    setState(prevState => ({
      config: {
        ...prevState.config,
        spectrum: {
          loading: true
        }
      }
    }));
    getTagSpectrum(tag.id, params, null).then(
      (res) => {
        setState(prevState => ({
          config: {
            ...prevState.config,
            spectrum: res
          }
        }));
      });

    setState(prevState => ({
      config: {
        ...prevState.config,
        waveform: {
          loading: true
        }
      }
    }));
    getTagWaveform(tag.id, params, null)
      .then((res) => {
        setState(prevState => ({
          config: {
            ...prevState.config,
            waveform: res
          }
        }));
      });

    if (tag.type === 'vibration') {
      setState(prevState => ({
        config: {
          ...prevState.config,
          cepstrum: {
            loading: true
          }
        }
      }));
      getTagCepstrum(tag.id, params, null)
        .then((res) => {
          setState(prevState => ({
            config: {
              ...prevState.config,
              cepstrum: res
            }
          }));
        });
    }
  };

  const getMeasurementData = (tag_id, params) => {
    const { getTagSpectrum, getTagWaveform, getTagCepstrum } = getProps().assetDetailsActions;
    const { config, tag, logScale } = getState();
    if (params.amp_type === 'demod') {
      params.type = 'demod';
      params.amp_type = 'acceleration';
    }
    if (params.amp_type === 'normal') params.amp_type = 'velocity';
    if (tag.type === 'current' && _.isNil(params.log_scale)) params.log_scale = logScale;
    if (!config.spectrumFeatures) {
      getProps().spectrumFeatureActions.getSpectrumFeatures(tag.id, config.config_id);
    }
    if (getProps().previewConfig) {
      updatePreviewConfig(tag, params);
    } else {
      getTagSpectrum(tag_id, params, config.config_id);
      getTagWaveform(tag_id, params, config.config_id);
      if (tag.type === 'vibration') getTagCepstrum(tag_id, params, config.config_id);
    }
    getSpectrumMarkers(params.timestamp, config, tag);
    setTimeStampInSession(params.timestamp);
  };

  const calendarSelect = (selectedRange, newCustomDayFrom = null, newCustomDayTo = null) => {
    const { config, amp_type, feature, measurement_type, tag, bin_nos, logScale } = getState();
    if (!config.trends_data) return;
    const newState = { selectedRange };
    const days = parseInt(selectedRange, 10);
    const params = {
      config_id: config.config_id,
      measurement_type
    };

    if (tag.type === 'current') params.log_scale = logScale;

    if (config && config.default && config.trends_config[0].tag_type === 'vibration' && !_.isEmpty(bin_nos)) params.bin_nos = bin_nos;

    if (config && config.default && config.trends_config[0].amp_type) {
      if (amp_type) params.amp_type = amp_type;
      else params.amp_type = config.trends_config[0].amp_type;
    }
    if (config && config.default && config.trends_config[0].feature) {
      if (feature) params.feature = feature;
      else params.feature = config.trends_config[0].feature;
    }

    if (params.amp_type === 'demod') {
      params.signal_type = 'demod';
      params.amp_type = 'acceleration';
    }
    if (params.amp_type === 'normal') params.amp_type = 'velocity';
    if (tag.type !== 'vibration') delete params.amp_type;
    if (measuredPeakForAlarmForm({
      openOverallAlarm: getState().openOverallAlarm, feature: params.feature, tag, account: getProps().currentAccount
    })) params.peak_preference = 'true_peak';

    if (selectedRange === 'custom' && newCustomDayFrom && newCustomDayTo) {
      params.from_timestamp = newCustomDayFrom;
      params.to_timestamp = newCustomDayTo;
      newState.customDayFrom = newCustomDayFrom;
      newState.customDayTo = newCustomDayTo;
    } else {
      newState.customDayFrom = null;
      newState.customDayTo = null;
    }
    if (!Number.isNaN(days)) {
      params.days = days;
    }
    newState.days = params.days;
    if (selectedRange === 'custom') {
      newState.days = 'custom';
    }
    setState({ paginationLoading: true });
    paginateAndFetchChartData(params, (res) => {
      if (!res) return;
      const tag_id = res[0].tag_id;
      const trendData = res[0].trend_data;
      if (trendData.length > 0 && tag.structure_type === 'vector') {
        const lastPointTimestamp = trendData[trendData.length - 1].x;
        const measurementParams = {
          tag_id,
          timestamp: lastPointTimestamp
        };
        if (amp_type) measurementParams.amp_type = amp_type;
        if (feature) measurementParams.feature = feature;
        getMeasurementData(tag_id, measurementParams);
      }
    }, [getState().tag], fetchChartData).then(
      () => setState({ paginationLoading: false })
    );
    setState({
      ...newState,
      calendarExpanded: false
    }, () => setCalendarInSession());
  };

  const refreshChart = (point = undefined) => {
    const { config, tag, amp_type, feature, waveAndSpecSelection } = getState();
    if (!config || !config.trends_data || !Array.isArray(config.trends_data) || !config.trends_data[0] || !config.trends_data[0].trend_data || !Array.isArray(config.trends_data[0].trend_data) || !tag) {
      return;
    }
    const params = getParamsForChartsData();
    const selectedIdx = waveAndSpecSelection
      ? config.trends_data[0].trend_data.findIndex(d => _.isEqual(d.x, waveAndSpecSelection.x))
      : -1;
    const selectedIdxSafe = selectedIdx >= 0 ? selectedIdx : 0;
    if (tag.type !== 'vibration') delete params.amp_type;
    if (params.amp_type === 'normal') params.amp_type = 'velocity';

    setState({ paginationLoading: true });
    paginateAndFetchChartData(params, (res) => {
      if (!res) return;
      const tag_id = res[0].tag_id;
      const trendData = res[0].trend_data;
      if (trendData.length > 0 && tag.structure_type === 'vector') {
        const lastPointTimestamp = trendData[trendData.length - 1].x;
        const measurementParams = {
          tag_id
        };
        if (amp_type) measurementParams.amp_type = amp_type;
        if (feature) measurementParams.feature = feature;
        if (!(point && point.afterOutlier)) {
          if (waveAndSpecSelection && waveAndSpecSelection.measurement_id) {
            const { x, measurement_id } = waveAndSpecSelection;
            const idx = trendData.findIndex(d => d.measurement_id === measurement_id);
            if (idx > -1) {
              setSelection(trendData[idx]);
            }
            measurementParams.timestamp = x;
          } else {
            measurementParams.timestamp = lastPointTimestamp;
          }
        } else {
          const idx = trendData.length <= selectedIdxSafe ? trendData.length - 1 : selectedIdxSafe;
          setState({
            waveAndSpecSelection: trendData[idx],
          });
          measurementParams.timestamp = trendData[idx].x;
        }
        getMeasurementData(tag_id, measurementParams);
      }

      if (trendData.length > 0 && tag.structure_type === 'scalar' && point && point.afterOutlier) {
        const idx = trendData.length <= selectedIdxSafe ? trendData.length - 1 : selectedIdxSafe;
        setState({
          waveAndSpecSelection: trendData[idx],
        });
      }
    }, [getState().tag], fetchChartData).then(
      () => setState({ paginationLoading: false })
    );
  };

  const getChartData = (newparams) => {
    const props = getProps();
    const { config, tag, amp_type, feature, days, customDayFrom, customDayTo, measurement_type, waveAndSpecSelection, bin_nos, logScale } = getState();
    let params = {
      amp_type: props.previewConfig ? props.searchParams.amp_type : amp_type,
      feature,
      config_id: config.config_id,
      measurement_type,
      bin_nos,
      ...newparams
    };
    params = utils.sanitizeAmpTypeParam(params, tag);
    if (params.amp_type === 'demod') {
      params.signal_type = 'demod';
    }
    if (_.isNil(params.log_scale) && tag.type === 'current') params.log_scale = logScale;

    if (days === 'custom') {
      params.from_timestamp = customDayFrom;
      params.to_timestamp = customDayTo;
    } else {
      params.days = days;
    }
    if (
      (newparams.amp_type && amp_type !== newparams.amp_type) ||
      (newparams.feature && feature !== newparams.feature) ||
      (newparams.measurement_type !== undefined && newparams.measurement_type !== measurement_type) ||
      (newparams.bin_nos !== bin_nos) ||
      (!_.isNil(newparams.logScale) && newparams.logScale !== logScale)
    ) {
      if (waveAndSpecSelection) newparams.timestamp = waveAndSpecSelection.x;

      const isAmplitudeTypeChange = newparams.amp_type && amp_type !== newparams.amp_type;
      const isFeatureChange = newparams.feature && feature !== newparams.feature;
      const state = getState();
      const hasOverlayPoints = state.showOverlay && state.selectedPoints.length > 0;

      if (hasOverlayPoints && isAmplitudeTypeChange) {
        const featureForSpectrum = isFeatureChange ? newparams.feature : undefined;
        refreshOverlayTraces(newparams.amp_type, amp_type, featureForSpectrum);
      } else if (hasOverlayPoints && isFeatureChange) {
        refreshOverlayPointsWithNewFeature(newparams.feature);
      } else if (!newparams.measurement_type && !newparams.bin_nos) {
        getWaveformAndSpectrum(newparams);
      }

      setState({
        amp_type: params.amp_type,
        feature: params.feature,
        measurement_type: params.measurement_type,
        bin_nos: params.bin_nos,
        logScale: !_.isNil(params.log_scale) ? params.log_scale : logScale
      }, () => {
        setChartSettingsInSession();
        setBinNosInSession();
      });
      if (config && config.default && !config.trends_config[0].amp_type) {
        delete params.amp_type;
      }
      if (config && config.default && !config.trends_config[0].feature) {
        delete params.feature;
      }
      if (config && config.default && !['vibration', 'current'].includes(config.trends_config[0].tag_type)) {
        delete params.measurement_type;
        delete params.bin_nos;
      } else if (_.isEmpty(params.bin_nos)) {
        delete params.bin_nos;
      }
      if (params.amp_type === 'demod') params.amp_type = 'acceleration';
      if (params.amp_type === 'normal') params.amp_type = 'velocity';
      if (tag.type !== 'vibration') delete params.amp_type;
      if (measuredPeakForAlarmForm({
        openOverallAlarm: getState().openOverallAlarm, feature: params.feature, tag, account: props.currentAccount
      })) params.peak_preference = 'true_peak';
      setState({ paginationLoading: true });
      paginateAndFetchChartData(params, (data) => {
        if (!data) return;
        const trendData = data[0].trend_data;

        const isAmplitudeTypeChangeInner = newparams.amp_type && amp_type !== newparams.amp_type;
        const isFeatureChangeInner = newparams.feature && feature !== newparams.feature;
        const currentState = getState();

        if ((isAmplitudeTypeChangeInner || isFeatureChangeInner) && currentState.showOverlay && currentState.selectedPoints.length > 0) {
          const updatedPoints = currentState.selectedPoints.map((point) => {
            if (!point.x) return point;
            const newPointData = trendData.find(d => areTimestampsEqual(d.x, point.x.i || point.x));
            if (newPointData) {
              return { ...point, y: newPointData.y };
            }
            return point;
          }).filter(Boolean);

          if (updatedPoints.length > 0) {
            updateSelectedPoints(updatedPoints);
          }
        }

        if (waveAndSpecSelection && waveAndSpecSelection.measurement_id && !newparams.measurement_type && !newparams.bin_nos) {
          const { measurement_id } = waveAndSpecSelection;
          const idx = trendData.findIndex(d => d.measurement_id === measurement_id);
          if (idx > -1) {
            setSelection(trendData[idx]);
          }
        } else {
          const dataPoint = getDataFromTimestamp(trendData) || trendData[trendData.length - 1];
          if (!dataPoint) return;
          newparams.timestamp = dataPoint.x;
          getWaveformAndSpectrum(newparams);
          setSelection(dataPoint);
        }
      }, [getState().tag], fetchChartData).then(
        () => setState({ paginationLoading: false })
      );
    }
    return null;
  };

  return {
    fetchChartData,
    updatePreviewConfig,
    getMeasurementData,
    calendarSelect,
    refreshChart,
    getChartData
  };
}
